import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isRealSite, withRealPrefix } from '../utils/siteMode';

const MOBILE_BREAKPOINT = 768;

function Header() {
  const { user, isAuthenticated, loginWithQQ, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isReal = isRealSite(location.pathname);

  // 检测移动端
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // 点击外部关闭下拉菜单
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 移动端菜单打开时禁止背景滚动
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout();
    setShowDropdown(false);
    setMobileMenuOpen(false);
    navigate(isReal ? withRealPrefix('/Home') : '/');
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const navLinks = isReal
    ? [
        { to: withRealPrefix('/Home'), label: '首页' },
        { to: withRealPrefix('/products'), label: '产品' },
        { to: withRealPrefix('/forum'), label: '社区' },
        { to: withRealPrefix('/about'), label: '关于' },
      ]
    : [
        { to: '/', label: '首页' },
        { to: '/products', label: '产品' },
        { to: '/about', label: '关于' },
      ];

  return (
    <header style={styles.header}>
      <div style={styles.leftSection}>
        <Link to={isReal ? withRealPrefix('/Home') : '/'} style={styles.logoLink}>
          <img 
            src={`${process.env.PUBLIC_URL}/smalllogo.png`} 
            alt="logo" 
            style={styles.logo} 
          />
          <span style={styles.title}>{isReal ? '蓝卡模玩社区' : '蓝卡模玩工作室'}</span>
        </Link>
      </div>

      {/* 桌面端导航 */}
      {!isMobile && (
        <nav style={styles.nav}>
          {navLinks.map(({ to, label }) => (
            <Link key={to} to={to} style={styles.navLink}>{label}</Link>
          ))}
        </nav>
      )}

      <div style={styles.rightSection}>
        {isReal && !isMobile && isAuthenticated && user ? (
          // 桌面端已登录：显示用户头像和昵称
          <div style={styles.userContainer} ref={dropdownRef}>
            <button 
              style={styles.userButton}
              onClick={() => setShowDropdown(!showDropdown)}
            >
              <img 
                src={user.avatar || user.figureurl_qq_2 || user.figureurl_2 || `${process.env.PUBLIC_URL}/default-avatar.png`}
                alt="头像"
                style={styles.avatar}
              />
              <span style={styles.nickname}>
                {user.nickname || user.name || '用户'}
              </span>
              <span style={styles.dropdownArrow}>▼</span>
            </button>

            {showDropdown && (
              <div style={styles.dropdown}>
                <div style={styles.dropdownHeader}>
                  <img 
                    src={user.avatar || user.figureurl_qq_2 || user.figureurl_2 || `${process.env.PUBLIC_URL}/default-avatar.png`}
                    alt="头像"
                    style={styles.dropdownAvatar}
                  />
                  <div style={styles.dropdownUserInfo}>
                    <div style={styles.dropdownNickname}>{user.nickname || user.name || '用户'}</div>
                    {user.openid && <div style={styles.dropdownId}>ID: {user.openid.slice(0, 8)}...</div>}
                  </div>
                </div>
                <div style={styles.dropdownDivider}></div>
                <button style={styles.dropdownItem} onClick={() => { setShowDropdown(false); navigate(withRealPrefix('/profile')); }}>
                  👤 个人中心
                </button>
                <button style={styles.dropdownItem} onClick={() => { setShowDropdown(false); navigate(withRealPrefix('/settings')); }}>
                  ⚙️ 设置
                </button>
                <div style={styles.dropdownDivider}></div>
                <button style={{...styles.dropdownItem, color: '#ff4d4f'}} onClick={handleLogout}>
                  🚪 退出登录
                </button>
              </div>
            )}
          </div>
        ) : isReal && !isMobile ? (
          // 桌面端未登录：显示登录按钮
          <button style={styles.loginButton} onClick={loginWithQQ}>
            <img 
              src={`${process.env.PUBLIC_URL}/Connect_logo_3.png`}
              alt="QQ登录"
              style={styles.qqIcon}
            />
            <span>登录</span>
          </button>
        ) : null}

        {/* 移动端汉堡菜单按钮 */}
        {isMobile && (
          <button
            style={styles.hamburgerButton}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? '关闭菜单' : '打开菜单'}
          >
            <span style={{ ...styles.hamburgerLine, transform: mobileMenuOpen ? 'rotate(45deg) translate(6px, 6px)' : 'none' }} />
            <span style={{ ...styles.hamburgerLine, opacity: mobileMenuOpen ? 0 : 1 }} />
            <span style={{ ...styles.hamburgerLine, transform: mobileMenuOpen ? 'rotate(-45deg) translate(6px, -6px)' : 'none' }} />
          </button>
        )}
      </div>

      {/* 移动端侧滑菜单 */}
      {isMobile && mobileMenuOpen && (
        <>
          <div style={styles.mobileOverlay} onClick={closeMobileMenu} aria-hidden="true" />
          <div style={styles.mobileMenu}>
            <div style={styles.mobileMenuHeader}>
              <span style={styles.mobileMenuTitle}>菜单</span>
              <button style={styles.mobileMenuClose} onClick={closeMobileMenu} aria-label="关闭">✕</button>
            </div>
            <nav style={styles.mobileNav}>
              {navLinks.map(({ to, label }) => (
                <Link key={to} to={to} style={styles.mobileNavLink} onClick={closeMobileMenu}>
                  {label}
                </Link>
              ))}
            </nav>
            <div style={styles.mobileMenuFooter}>
              {isReal && isAuthenticated && user ? (
                <>
                  <div style={styles.mobileUserInfo}>
                    <img src={user.avatar || user.figureurl_qq_2 || user.figureurl_2 || `${process.env.PUBLIC_URL}/default-avatar.png`} alt="" style={styles.mobileAvatar} />
                    <span>{user.nickname || user.name || '用户'}</span>
                  </div>
                  <button style={styles.mobileMenuItem} onClick={() => { closeMobileMenu(); navigate(withRealPrefix('/profile')); }}>
                    👤 个人中心
                  </button>
                  <button style={styles.mobileMenuItem} onClick={() => { closeMobileMenu(); navigate(withRealPrefix('/settings')); }}>
                    ⚙️ 设置
                  </button>
                  <button style={{ ...styles.mobileMenuItem, color: '#ff4d4f' }} onClick={handleLogout}>
                    🚪 退出登录
                  </button>
                </>
              ) : isReal ? (
                <button style={styles.mobileLoginButton} onClick={() => { closeMobileMenu(); loginWithQQ(); }}>
                  <img src={`${process.env.PUBLIC_URL}/Connect_logo_3.png`} alt="" style={styles.qqIcon} />
                  QQ 登录
                </button>
              ) : null}
            </div>
          </div>
        </>
      )}
    </header>
  );
}

const styles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#1a1d23',
    padding: '0 24px',
    height: '60px',
    borderBottom: '1px solid #2d3139',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
  },
  logoLink: {
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
    color: 'white',
  },
  logo: {
    height: '36px',
  },
  title: {
    marginLeft: '12px',
    fontSize: '18px',
    fontWeight: '600',
    color: '#fff',
  },
  nav: {
    display: 'flex',
    gap: '32px',
  },
  navLink: {
    color: '#a0a6b1',
    textDecoration: 'none',
    fontSize: '15px',
    fontWeight: '500',
    transition: 'color 0.2s',
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  hamburgerButton: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '5px',
    width: '40px',
    height: '40px',
    padding: 0,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
  },
  hamburgerLine: {
    display: 'block',
    width: '22px',
    height: '2px',
    backgroundColor: '#fff',
    borderRadius: 1,
    transition: 'transform 0.2s, opacity 0.2s',
  },
  mobileOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 1001,
  },
  mobileMenu: {
    position: 'fixed',
    top: 0,
    right: 0,
    width: '280px',
    maxWidth: '85vw',
    height: '100vh',
    backgroundColor: '#1a1d23',
    boxShadow: '-4px 0 20px rgba(0,0,0,0.3)',
    zIndex: 1002,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  mobileMenuHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px',
    borderBottom: '1px solid #2d3139',
  },
  mobileMenuTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#fff',
  },
  mobileMenuClose: {
    width: '36px',
    height: '36px',
    padding: 0,
    background: 'transparent',
    border: 'none',
    color: '#9ca3af',
    fontSize: '20px',
    cursor: 'pointer',
  },
  mobileNav: {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px 0',
  },
  mobileNavLink: {
    padding: '14px 20px',
    color: '#d1d5db',
    textDecoration: 'none',
    fontSize: '16px',
    transition: 'background 0.2s',
  },
  mobileMenuFooter: {
    marginTop: 'auto',
    padding: '20px',
    borderTop: '1px solid #2d3139',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  mobileUserInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 0',
    color: '#fff',
    fontSize: '15px',
  },
  mobileAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    objectFit: 'cover',
  },
  mobileMenuItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 0',
    background: 'transparent',
    border: 'none',
    color: '#d1d5db',
    fontSize: '15px',
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
  },
  mobileLoginButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '14px',
    background: 'linear-gradient(135deg, #12B7F5 0%, #0099E5 100%)',
    border: 'none',
    borderRadius: '12px',
    color: 'white',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  loginButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'linear-gradient(135deg, #12B7F5 0%, #0099E5 100%)',
    border: 'none',
    borderRadius: '20px',
    padding: '8px 20px',
    color: 'white',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  qqIcon: {
    height: '20px',
    width: 'auto',
  },
  userContainer: {
    position: 'relative',
  },
  userButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: 'transparent',
    border: '1px solid #3d4450',
    borderRadius: '24px',
    padding: '6px 16px 6px 6px',
    cursor: 'pointer',
    transition: 'background 0.2s, border-color 0.2s',
  },
  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    objectFit: 'cover',
  },
  nickname: {
    color: '#fff',
    fontSize: '14px',
    fontWeight: '500',
    maxWidth: '100px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  dropdownArrow: {
    color: '#6b7280',
    fontSize: '10px',
    marginLeft: '4px',
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    width: '220px',
    backgroundColor: '#252830',
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
    border: '1px solid #3d4450',
    overflow: 'hidden',
    animation: 'fadeIn 0.15s ease',
  },
  dropdownHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#1e2128',
  },
  dropdownAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    objectFit: 'cover',
  },
  dropdownUserInfo: {
    flex: 1,
    overflow: 'hidden',
  },
  dropdownNickname: {
    color: '#fff',
    fontSize: '15px',
    fontWeight: '600',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  dropdownId: {
    color: '#6b7280',
    fontSize: '12px',
    marginTop: '2px',
  },
  dropdownDivider: {
    height: '1px',
    backgroundColor: '#3d4450',
  },
  dropdownItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#d1d5db',
    fontSize: '14px',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background 0.15s',
  },
};

export default Header;
